// podtemat1_walec_types/LateralSurfaceAreaOfCylinderTask.js
// Pole powierzchni bocznej walca: Pb = 2πrh.
// Akceptujemy wynik dokładny (k*pi) albo liczbowy (±0.01).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import PiInput, { formatPiValueLatex } from "../../../../components/inputs/PiInput";

const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const nearlyAbs = (a, b, eps) => Math.abs(a - b) <= eps;

// Fallbackowy parser dla zgodności wstecz (gdyby przyszło raw bez parsed)
function parseNumericOrKPi(raw) {
  if (!raw) return null;
  let s = String(raw).trim().replace(",", ".").replace(/\s/g, "");
  if (/^[\+\-]?\d+(\.\d+)?$/.test(s)) return { kind: "numeric", value: Number(s) };
  s = s.replace(/π/gi, "pi");
  let m = s.match(/^\(?([\+\-]?\d+(\.\d+)?)\)?\*?pi$/i); if (m) return { kind: "kpi", k: Number(m[1]) };
  m = s.match(/^pi\*?\(?([\+\-]?\d+(\.\d+)?)\)?$/i);     if (m) return { kind: "kpi", k: Number(m[1]) };
  return null;
}

export function generateTask() {
  // r: 2..8, h: 4..12
  const r = pick(range(2, 8));
  const h = pick(range(4, 12));
  const k = 2 * r * h;         // współczynnik przy π
  const numeric = k * Math.PI;
  return { type: "LateralSurfaceAreaOfCylinderTask", data: { r, h, k, numeric } };
}

export function renderTask({ data }) {
  const { r, h } = data;
  return (
    <div style={{ marginBottom: 12 }}>
      Oblicz <b>pole powierzchni bocznej</b> walca o promieniu podstawy{" "}
      <InlineMath math={`${r}`} /> i wysokości <InlineMath math={`${h}`} />.
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <PiInput
      value={userAnswer}
      onChange={(ans) => onChange?.(ans)}   // PiInput zwraca { raw, parsed }
      disabled={disabled}
      label="\mathrm{P}_{\mathrm{b}}"
      placeholder="np. 12π, 15*pi, 37,70"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  // 1) preferuj liczbową wartość z PiInput (już po przeliczeniu π)
  const parsedFromPi = typeof userAnswer.parsed === "number" ? userAnswer.parsed : null;

  // 2) fallback – stary parser
  const fallbackParsed = (() => {
    if (!userAnswer.raw) return null;
    const p = parseNumericOrKPi(userAnswer.raw);
    if (!p) return null;
    return p.kind === "kpi" ? p.k * Math.PI : p.value;
  })();

  const val = Number.isFinite(parsedFromPi) ? parsedFromPi : fallbackParsed;
  if (!Number.isFinite(val)) return false;

  const usedPi = /pi|π/i.test(String(userAnswer.raw || ""));
  const eps = usedPi ? 1e-6 : 0.0100001; // π → bardzo ciasno; liczba → ±0,01
  return nearlyAbs(val, data.numeric, eps);
}

export function renderUserAnswer(userAnswer) {
  const raw = userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  const valLatex = formatPiValueLatex(raw) || String(raw);
  return <InlineMath math={`\\displaystyle \\mathrm{P}_{\\mathrm{b}} = ${valLatex}`} />;
}

export function renderAnswer({ data }) {
  const exact = `\\mathrm{P}_{\\mathrm{b}}=${data.k}\\pi`;
  return <InlineMath math={exact} />;
}

export function renderExplanation({ data }) {
  const { r, h, k } = data;
  const twoR = 2 * r;
  const step1 = `O=2\\pi r=2\\pi\\cdot ${r}=${twoR}\\pi`;
  const step2 = `\\mathrm{P}_{\\mathrm{b}}=O\\cdot h=${twoR}\\pi\\cdot ${h}=${k}\\pi`;

  return (
    <div style={{ display: "grid", rowGap: 8 }}>
      <p><strong>Krok 1:</strong> Obwód podstawy (okręgu):</p>
      <BlockMath math={'{'+step1+'}'} />
      <p><strong>Krok 2:</strong> Pole powierzchni bocznej:</p>
      <BlockMath math={'{'+step2+'}'} />
    </div>
  );
}
